import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useSyncRoomAmenities } from '~/hooks/apis/accommodation/useSyncRoomAmenities';

import type { OfferBuilderRoom, RoomAmenity } from '../types';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
}

function getAmenityName(amenity: RoomAmenity) {
  return typeof amenity === 'string' ? amenity : amenity.name;
}

function getAmenityNames(amenities: Array<RoomAmenity> = []) {
  return amenities.map(getAmenityName).filter((amenityName): amenityName is string => !!amenityName);
}

export default function AmenitiesTab({ onChange, room }: Props) {
  const [customAmenity, setCustomAmenity] = useState('');
  const { data: amenities = [], isLoading: isLoadingAmenities } = useGetAmenities();
  const selectedAmenityNames = useMemo(() => getAmenityNames(room.amenities), [room.amenities]);
  const selectedAmenityNamesSet = useMemo(() => new Set(selectedAmenityNames), [selectedAmenityNames]);
  const amenityOptions = useMemo(() => {
    const amenityNames = amenities
      .map((amenity) => amenity.name)
      .filter((amenityName): amenityName is string => typeof amenityName === 'string');

    return Array.from(new Set<string>(amenityNames.concat(selectedAmenityNames))).sort((amenityName, nextAmenityName) =>
      amenityName.toLowerCase().localeCompare(nextAmenityName.toLowerCase()),
    );
  }, [amenities, selectedAmenityNames]);
  const syncRoomAmenitiesMutation = useSyncRoomAmenities({
    onSuccess: (amenities) => onChange({ amenities: getAmenityNames(amenities) }),
  });

  const updateAmenity = (amenity: string, checked: boolean) => {
    onChange({
      amenities: checked
        ? Array.from(new Set(selectedAmenityNames.concat(amenity)))
        : selectedAmenityNames.filter((selectedAmenity) => selectedAmenity !== amenity),
    });
  };

  const addCustomAmenity = () => {
    const amenity = customAmenity.trim();

    if (!amenity) {
      return;
    }

    updateAmenity(amenity, true);
    setCustomAmenity('');
  };

  const syncAmenities = () => {
    if (!room.property_id || !room.id) {
      return;
    }

    syncRoomAmenitiesMutation.mutate({
      reservationPropertyId: room.property_id,
      reservationRoomId: room.id,
    });
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Toggle the amenities present in this room. Use <Box component="em">Sync</Box> to copy from the property
        defaults.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
        }}
      >
        {isLoadingAmenities ? (
          <Typography color="text.secondary">Loading amenities...</Typography>
        ) : (
          amenityOptions.map((amenity) => (
            <FormControlLabel
              key={amenity}
              control={
                <Checkbox
                  checked={selectedAmenityNamesSet.has(amenity)}
                  onChange={(event) => updateAmenity(amenity, event.target.checked)}
                />
              }
              label={amenity}
            />
          ))
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Custom amenity"
          value={customAmenity}
          onChange={(event) => setCustomAmenity(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addCustomAmenity();
            }
          }}
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addCustomAmenity}>
          Add custom amenity
        </Button>
        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          disabled={!room.property_id || !room.id || syncRoomAmenitiesMutation.isPending}
          onClick={syncAmenities}
        >
          {syncRoomAmenitiesMutation.isPending ? 'Syncing...' : 'Sync room amenities'}
        </Button>
      </Stack>
    </Stack>
  );
}
